import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // React Router 사용
import { useHeader } from "../../contexts/HeaderContext";
import {
  HeaderContainer,
  LogoWrapper,
  NavContainer,
  NavItem,
  NavText,
  HamburgerButton,
  MobileNav,
  MobileNavContent,
  MobileNavItem,
  CloseButton,
  MobileHeader,
  MobileSNS,
  SNSIcon,
  MobileMenuOverlay,
  MobileDivider,
} from "./styles";

const AppHeader: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate(); // 페이지 이동을 위한 hook
  const { isTransparent } = useHeader();

  // 화면 크기 변경 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      // 화면이 모바일 크기보다 커지면 메뉴 닫기
      if (window.innerWidth > 768 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isMobileMenuOpen]);

  // 스크롤 이벤트 리스너 추가
  useEffect(() => {
    const handleScroll = () => {
      // 스크롤 위치가 100px을 넘어가면 scrolled를 true로 설정
      const isScrolled = window.scrollY > 100;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [scrolled]);

  // 페이지 이동 함수
  const handleNavigation = (path: string) => {
    navigate(path);
    window.scrollTo(0, 0);
    setIsMobileMenuOpen(false); // 네비게이션 후 모바일 메뉴 닫기
  };

  // 모바일 메뉴 열기/닫기 시 body 스크롤 제어
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'; // 메뉴 열릴 때 스크롤 방지
      document.body.style.touchAction = 'none'; // 터치 동작 비활성화
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [isMobileMenuOpen]);

  // 메뉴 아이템 목록
  const menuItems = ['Works', 'About', 'Service', 'Contact'];

  return (
    <>
      <HeaderContainer isTransparent={isTransparent} isScrolled={scrolled}>
        <LogoWrapper onClick={() => handleNavigation("/")}>
          <img 
            src={isTransparent && !scrolled 
              ? "/images/logo-white.svg" 
              : "/images/logo-black.svg"
            } 
            alt="Logo" 
            width={isMobile ? "48" : "72"} 
            height={isMobile ? "24" : "36"} 
          />
        </LogoWrapper>
        
        <NavContainer>
          <nav style={{ display: 'flex', gap: '80px', justifyContent: 'flex-end' }}>
            {menuItems.map((text, index) => (
              <NavItem
                key={index}
                onClick={() => handleNavigation(`/${text.toLowerCase()}`)}
              >
                <NavText isTransparent={isTransparent} isScrolled={scrolled}>
                  {text}
                </NavText>
              </NavItem>
            ))}
          </nav>
        </NavContainer>
        
        <HamburgerButton 
          onClick={() => setIsMobileMenuOpen(true)}
          isOpen={isMobileMenuOpen}
        >
          <img 
            src={isTransparent && !scrolled ? "/images/common/header/menu-white.svg" : "/images/common/header/menu-black.svg"} 
            alt="Menu" 
          />
        </HamburgerButton>
      </HeaderContainer>

      <MobileMenuOverlay isOpen={isMobileMenuOpen} onClick={() => setIsMobileMenuOpen(false)} />

      <MobileNav isOpen={isMobileMenuOpen}>
        <MobileHeader>
          <LogoWrapper onClick={() => handleNavigation("/")}>
            <img 
              src="/images/logo-white.svg"
              alt="Logo" 
              width="48" 
              height="24" 
            />
          </LogoWrapper>
        </MobileHeader>

        <CloseButton onClick={() => setIsMobileMenuOpen(false)}>
          ✕
        </CloseButton>

        <MobileNavContent>
          {menuItems.map((text, index) => (
            <React.Fragment key={index}>
              <MobileNavItem
                id={text === 'Contact' ? 'mobile-contact-menu' : undefined}
                onClick={() => handleNavigation(`/${text.toLowerCase()}`)}
              >
                {text}
              </MobileNavItem>
              {text === 'Contact' && (
                <>
                  <MobileDivider id="mobile-divider" />
                  <MobileSNS style={{ marginTop: '6px' }}>
                    <SNSIcon href="https://www.youtube.com/@puzlofficial/" target="_blank" rel="noopener noreferrer">
                      <img src="/images/common/header/sns/youtube.svg" alt="YouTube" />
                    </SNSIcon>
                    <SNSIcon href="https://blog.naver.com/puzlcorp/" target="_blank" rel="noopener noreferrer">
                      <img src="/images/common/header/sns/naver-blog.svg" alt="Naver Blog" />
                    </SNSIcon>
                    <SNSIcon href="https://www.instagram.com/pu_zl_duck?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw%3D%3D" target="_blank" rel="noopener noreferrer">
                      <img src="/images/common/header/sns/instagram.svg" alt="Instagram" />
                    </SNSIcon>
                  </MobileSNS>
                </>
              )}
            </React.Fragment>
          ))}
        </MobileNavContent>
      </MobileNav>
    </>
  );
};

export default AppHeader;
